import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, filter, of, switchMap } from 'rxjs';

import { NotificationSettingsService } from '../../../../services/notification-settings/notification-settings.service';
import { UserService } from '../../../../services/user/user.service';
import { NotificationSettings } from '../../../../interfaces/notification-settings';
import { User } from '../../../../interfaces/user';
import { SlideToggleComponent } from '../../../../shared/slide-toggle/slide-toggle.component';
import { MessageModalService } from '../../../../services/message-modal/message-modal.service';
import { resolveErrorMessage } from '../../../../shared/utils/api-error.util';

type ToggleRow = {
  setting: keyof NotificationSettings;
  label: string;
};

const ALL_TOGGLES: ToggleRow[] = [
  {
    setting: 'NotifyOwnJobSuccess',
    label: $localize`Email me when my analysis jobs complete`,
  },
  {
    setting: 'NotifyOwnJobError',
    label: $localize`Email me when my analysis jobs fail`,
  },
  {
    setting: 'NotifyAdminOrgJobSuccess',
    label: $localize`Email me when any job in my organization completes`,
  },
  {
    setting: 'NotifyAdminOrgJobError',
    label: $localize`Email me when any job in my organization fails`,
  },
];

@Component({
  selector: 'app-notification-settings',
  standalone: true,
  imports: [SlideToggleComponent, MatProgressSpinnerModule],
  templateUrl: './notification-settings.component.html',
  styleUrl: './notification-settings.component.css',
})
export class NotificationSettingsComponent implements OnInit {
  private notificationSettingsService = inject(NotificationSettingsService);
  private userService = inject(UserService);
  private messageModalService = inject(MessageModalService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  isLoading = true;
  loadFailed = false;
  /** Key of the toggle whose PATCH is in flight; that toggle spins, the others disable. */
  savingKey: keyof NotificationSettings | null = null;
  settings: NotificationSettings | null = null;
  // The GET response is already role-filtered: non-admins get no admin keys,
  // so presence in `settings` decides which toggles render.
  toggles: ToggleRow[] = [];

  ngOnInit(): void {
    // Settings are stored per org membership, so re-fetch whenever the user's data
    // context changes. user$ re-emits for every auth change (org switch, token-less
    // refreshes like EULA acceptance), so all of that re-fetch policy is inherited.
    this.userService.user$
      .pipe(
        filter((user): user is User => user !== null),
        switchMap(() => {
          this.isLoading = true;
          this.loadFailed = false;
          return this.notificationSettingsService.getNotificationSettings().pipe(
            // Catch per fetch so one failure doesn't end the outer subscription.
            catchError((error) => {
              console.error('Failed to load notification settings', error);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((settings) => {
        if (settings) {
          // Copy so optimistic toggle writes never mutate an object the service handed out.
          this.settings = { ...settings };
          this.toggles = ALL_TOGGLES.filter((row) => settings[row.setting] !== undefined);
        } else {
          this.settings = null;
          this.toggles = [];
          this.loadFailed = true;
        }
        this.isLoading = false;
      });
  }

  isChecked(row: ToggleRow): boolean {
    return !!this.settings?.[row.setting];
  }

  handleToggle(row: ToggleRow, checked: boolean): void {
    if (!this.settings || this.savingKey) {
      return;
    }
    // Only rendered toggles fire, so the key is always present; ?? false narrows the optional admin keys.
    const previous = this.settings[row.setting] ?? false;
    // Optimistic update so the toggle reflects the new state; reverted on error.
    this.settings[row.setting] = checked;
    this.savingKey = row.setting;

    this.notificationSettingsService.updateNotificationSettings({ [row.setting]: checked }).subscribe({
      next: () => {
        this.savingKey = null;
        this.snackBar.open($localize`Notification settings saved`, undefined, { duration: 2000 });
      },
      error: (error) => {
        this.settings![row.setting] = previous;
        this.savingKey = null;
        this.messageModalService.openModal(
          $localize`Error`,
          resolveErrorMessage(error, $localize`Failed to save notification settings. Please try again.`)
        );
      },
    });
  }
}
